import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import type { CollectionKind, CollectionSummary, Space } from "../api/types";
import { connectorLabel } from "../lib/format";
import {
  createCollection,
  createSpace,
  loadArchived,
  logout,
  renameCollection,
  renameSpace,
  selectOrg,
  setCollectionArchived,
  setCurrentSpace,
  setSpaceArchived,
  toastError,
} from "../store/app";
import { useApp } from "../store/hooks";
import { MenuButton, useContextMenu, type MenuEntry } from "./ContextMenu";
import { LifecycleDialog, type LifecycleTarget } from "./LifecycleDialogs";
import { Avatar } from "./bits";
import { toggleBuckets } from "./BucketDialogs";
import { Icon } from "./icons";
import { ProfileDialog } from "./ProfileDialog";

function UserMenu({
  name,
  email,
  admin,
  onNavigate,
}: {
  name: string;
  email: string;
  admin: boolean;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const me = useApp((s) => s.me);
  const orgId = useApp((s) => s.orgId);
  const actor = me?.orgs.find((o) => o.org.id === orgId)?.actor;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        ref.current?.querySelector<HTMLButtonElement>(".user-btn")?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    ref.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const item = (label: string, icon: Parameters<typeof Icon>[0]["name"], run: () => void) => (
    <button
      type="button"
      role="menuitem"
      className="menu-item"
      onClick={() => {
        setOpen(false);
        run();
      }}
    >
      <Icon name={icon} />
      {label}
    </button>
  );

  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className="user-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title={email}
      >
        <Avatar actor={actor} size={24} />
        <span className="user-name">{name}</span>
        <Icon name="chevronUp" size={14} />
      </button>
      {open && (
        <div className="menu" role="menu" aria-label="Account">
          <div className="menu-head muted small">{email}</div>
          {item("Profile & time zone", "user", () => setProfile(true))}
          {admin &&
            item("Admin panel", "shield", () => {
              navigate("/admin");
              onNavigate?.();
            })}
          {item("Log out", "logout", async () => {
            await logout().catch(() => undefined);
            navigate("/login");
          })}
        </div>
      )}
      {profile && <ProfileDialog onClose={() => setProfile(false)} />}
    </div>
  );
}

function InlineInput({
  label,
  placeholder,
  onSubmit,
  onCancel,
  extra,
}: {
  label: string;
  placeholder: string;
  onSubmit: (value: string) => Promise<void>;
  onCancel: () => void;
  extra?: React.ReactNode;
}) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="inline-create"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!value.trim()) return;
        setBusy(true);
        try {
          await onSubmit(value.trim());
        } finally {
          setBusy(false);
        }
      }}
    >
      <input
        autoFocus
        aria-label={label}
        placeholder={placeholder}
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && onCancel()}
      />
      {extra}
      <div className="inline-create-actions">
        <button type="submit" className="btn small primary" disabled={busy || !value.trim()}>
          Add
        </button>
        <button type="button" className="btn small" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Inline rename field: Enter saves, Esc / blur without change cancels. */
function RenameInput({
  initial,
  label,
  onSubmit,
  onDone,
}: {
  initial: string;
  label: string;
  onSubmit: (value: string) => Promise<void>;
  onDone: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const commit = async () => {
    const v = value.trim();
    if (!v || v === initial) return onDone();
    setBusy(true);
    try {
      await onSubmit(v);
      onDone();
    } catch (e) {
      toastError(e, "Rename failed");
      setBusy(false);
      ref.current?.focus();
    }
  };
  return (
    <form
      className="rename-form"
      onSubmit={(e) => {
        e.preventDefault();
        void commit();
      }}
    >
      <input
        ref={ref}
        aria-label={label}
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onDone();
          }
        }}
        onBlur={() => !busy && void commit()}
      />
    </form>
  );
}

function collectionEntries(
  c: CollectionSummary,
  archivedView: boolean,
  actions: {
    open: () => void;
    rename: () => void;
    dialog: (t: LifecycleTarget) => void;
  },
): MenuEntry[] {
  const archived = archivedView || !!c.archived_at;
  const entries: MenuEntry[] = [{ label: "Open", icon: "expand", onSelect: actions.open }];
  if (!archived) {
    entries.push(
      { label: "Rename", icon: "edit", onSelect: actions.rename },
      {
        label: c.buckets_enabled ? "Disable buckets" : "Enable buckets",
        icon: "board",
        onSelect: () => toggleBuckets(c, () => actions.dialog({ kind: "enable-buckets", collection: c })),
      },
    );
  }
  entries.push(
    archived
      ? {
          label: "Unarchive",
          icon: "unarchive",
          onSelect: () =>
            void setCollectionArchived(c.id, false, true).catch((e) => toastError(e, "Unarchive failed")),
        }
      : {
          label: "Archive",
          icon: "archive",
          onSelect: () => {
            if (c.bindings.length) actions.dialog({ kind: "archive-collection", collection: c });
            else void setCollectionArchived(c.id, true, true).catch((e) => toastError(e, "Archive failed"));
          },
        },
    { separator: true },
    { label: "Delete…", icon: "trash", danger: true, onSelect: () => actions.dialog({ kind: "delete-collection", collection: c }) },
  );
  return entries;
}

function CollectionRow({
  c,
  hint,
  archivedView,
  onNavigate,
  onDialog,
}: {
  c: CollectionSummary;
  hint?: string;
  archivedView?: boolean;
  onNavigate?: () => void;
  onDialog: (t: LifecycleTarget) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const navigate = useNavigate();
  const open = () => {
    if (!archivedView) setCurrentSpace(c.space_id);
    navigate(`/c/${c.id}`);
    onNavigate?.();
  };
  const menu = useContextMenu(
    () => collectionEntries(c, !!archivedView, { open, rename: () => setRenaming(true), dialog: onDialog }),
    `${c.name} actions`,
  );
  if (renaming) {
    return (
      <li className="nav-row renaming">
        <RenameInput
          initial={c.name}
          label="Collection name"
          onSubmit={async (name) => void (await renameCollection(c.id, c.space_id, name))}
          onDone={() => setRenaming(false)}
        />
      </li>
    );
  }
  return (
    <li className={`nav-row${menu.isOpen ? " menu-open" : ""}${archivedView ? " archived" : ""}`}>
      <NavLink
        to={`/c/${c.id}`}
        onClick={() => {
          if (!archivedView) setCurrentSpace(c.space_id);
          onNavigate?.();
        }}
        aria-keyshortcuts="Shift+F10"
        {...menu.targetProps}
      >
        <span className="col-name">{c.name}</span>
        {hint && <span className="muted small">{hint}</span>}
        {c.bindings.map((b) => (
          <span key={b.id} className="badge tiny" title={`${b.remote_collection_name} (${b.mode})`}>
            {connectorLabel(b.connector_type)}
          </span>
        ))}
        {!archivedView && c.open_count > 0 && <span className="count">{c.open_count}</span>}
      </NavLink>
      <MenuButton label={`${c.name} actions`} expanded={menu.isOpen} onOpen={menu.openFrom} />
      {menu.menu}
    </li>
  );
}

function SpaceHead({
  space,
  cols,
  collapsed,
  archivedView,
  onToggle,
  onAddCollection,
  onDialog,
  onNavigate,
}: {
  space: Space;
  cols: CollectionSummary[];
  collapsed: boolean;
  archivedView?: boolean;
  onToggle: () => void;
  onAddCollection?: () => void;
  onDialog: (t: LifecycleTarget) => void;
  onNavigate?: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const navigate = useNavigate();
  const openOverview = () => {
    setCurrentSpace(space.id);
    navigate(`/s/${space.id}`);
    onNavigate?.();
  };
  const entries = (): MenuEntry[] =>
    archivedView
      ? [
          {
            label: "Unarchive space",
            icon: "unarchive",
            onSelect: () => void setSpaceArchived(space.id, false, true).catch((e) => toastError(e, "Unarchive failed")),
          },
          { separator: true },
          { label: "Delete space…", icon: "trash", danger: true, onSelect: () => onDialog({ kind: "delete-space", space, collections: cols }) },
        ]
      : [
          { label: "Open overview", icon: "board", onSelect: openOverview },
          { label: "Rename", icon: "edit", onSelect: () => setRenaming(true) },
          { label: "New collection", icon: "plus", onSelect: () => onAddCollection?.() },
          {
            label: "Archive space",
            icon: "archive",
            onSelect: () => {
              if (cols.some((c) => c.bindings.length)) onDialog({ kind: "archive-space", space, collections: cols });
              else void setSpaceArchived(space.id, true, true).catch((e) => toastError(e, "Archive failed"));
            },
          },
          { separator: true },
          { label: "Delete space…", icon: "trash", danger: true, onSelect: () => onDialog({ kind: "delete-space", space, collections: cols }) },
        ];
  const menu = useContextMenu(entries, `${space.name} actions`);
  if (renaming) {
    return (
      <div className="space-head renaming">
        <RenameInput
          initial={space.name}
          label="Space name"
          onSubmit={async (name) => void (await renameSpace(space.id, name))}
          onDone={() => setRenaming(false)}
        />
      </div>
    );
  }
  return (
    <div className={`space-head${menu.isOpen ? " menu-open" : ""}`}>
      {archivedView ? (
        <button
          type="button"
          className="space-toggle"
          aria-expanded={!collapsed}
          aria-keyshortcuts="Shift+F10"
          onClick={onToggle}
          {...menu.targetProps}
        >
          <span className="caret" aria-hidden="true">
            {collapsed ? "▸" : "▾"}
          </span>
          <span className="col-name">{space.name}</span>
        </button>
      ) : (
        <>
          {/* Chevron collapses / expands; the name opens the space overview. */}
          <button
            type="button"
            className="space-caret"
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? "Expand" : "Collapse"} ${space.name}`}
            onClick={onToggle}
          >
            <span className="caret" aria-hidden="true">
              {collapsed ? "▸" : "▾"}
            </span>
          </button>
          <NavLink
            to={`/s/${space.id}`}
            className="space-link"
            title={`${space.name}: all tasks`}
            aria-keyshortcuts="Shift+F10"
            onClick={() => {
              setCurrentSpace(space.id);
              onNavigate?.();
            }}
            {...menu.targetProps}
          >
            <span className="col-name">{space.name}</span>
          </NavLink>
        </>
      )}
      <MenuButton label={`${space.name} actions`} expanded={menu.isOpen} onOpen={menu.openFrom} />
      {onAddCollection && (
        <button
          type="button"
          className="icon-btn"
          aria-label={`New collection in ${space.name}`}
          title="New collection"
          onClick={onAddCollection}
        >
          +
        </button>
      )}
      {menu.menu}
    </div>
  );
}

function ArchivedSection({ onNavigate, onDialog }: { onNavigate?: () => void; onDialog: (t: LifecycleTarget) => void }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const archived = useApp((s) => s.archived);
  const activeSpaces = useApp((s) => s.spaces);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    loadArchived()
      .catch((e) => toastError(e, "Could not load archived items"))
      .finally(() => setLoading(false));
  }, [open]);
  const spaceName = (id: string) => activeSpaces?.find((s) => s.id === id)?.name;
  const looseCollections = (archived?.collections ?? []).filter(
    (c) => !archived?.spaces.some((s) => s.id === c.space_id),
  );
  const empty = archived && archived.spaces.length === 0 && looseCollections.length === 0;
  return (
    <div className="archived-section">
      <button type="button" className="archived-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="caret" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        Archived
      </button>
      {open && (
        <div className="archived-body">
          {loading && !archived && <p className="muted small pad">Loading…</p>}
          {empty && <p className="muted small pad">Nothing archived.</p>}
          {looseCollections.length > 0 && (
            <ul className="nav-list collections">
              {looseCollections.map((c) => (
                <CollectionRow key={c.id} c={c} hint={spaceName(c.space_id)} archivedView onNavigate={onNavigate} onDialog={onDialog} />
              ))}
            </ul>
          )}
          {archived?.spaces.map((sp) => {
            const cols = archived.collections.filter((c) => c.space_id === sp.id);
            return (
              <div key={sp.id} className="space archived">
                <SpaceHead
                  space={sp}
                  cols={cols}
                  archivedView
                  collapsed={!!collapsed[sp.id]}
                  onToggle={() => setCollapsed((c) => ({ ...c, [sp.id]: !c[sp.id] }))}
                  onDialog={onDialog}
                />
                {!collapsed[sp.id] && (
                  <ul className="nav-list collections">
                    {cols.map((c) => (
                      <CollectionRow key={c.id} c={c} archivedView onNavigate={onNavigate} onDialog={onDialog} />
                    ))}
                    {cols.length === 0 && <li className="muted small pad">No collections</li>}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const me = useApp((s) => s.me);
  const orgId = useApp((s) => s.orgId);
  const spaces = useApp((s) => s.spaces);
  const collections = useApp((s) => s.collections);
  const eventsConnected = useApp((s) => s.eventsConnected);
  const [addingSpace, setAddingSpace] = useState(false);
  const [addingCollectionIn, setAddingCollectionIn] = useState<string | null>(null);
  const [newKind, setNewKind] = useState<CollectionKind>("list");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [dialog, setDialog] = useState<LifecycleTarget | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const membership = me?.orgs.find((o) => o.org.id === orgId);
  const admin = membership?.role === "owner" || membership?.role === "admin";

  return (
    <nav className="sidebar" aria-label="Main navigation">
      <div className="sidebar-head">
        <span className="logo" aria-hidden="true">
          ✓
        </span>
        {me && me.orgs.length > 1 ? (
          <select
            className="org-select"
            aria-label="Organization"
            value={orgId ?? ""}
            onChange={(e) => selectOrg(e.target.value)}
          >
            {me.orgs.map((o) => (
              <option key={o.org.id} value={o.org.id}>
                {o.org.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="org-name">{membership?.org.name ?? "ToDoItAll"}</span>
        )}
        <span
          className={`live-dot ${eventsConnected ? "on" : "off"}`}
          title={eventsConnected ? "Realtime connected" : "Realtime disconnected"}
          role="img"
          aria-label={eventsConnected ? "Realtime connected" : "Realtime disconnected"}
        />
      </div>

      <ul className="nav-list">
        <li>
          <NavLink to="/my-tasks" onClick={onNavigate}>
            My tasks
          </NavLink>
        </li>
        <li>
          <NavLink to="/meetings" onClick={onNavigate}>
            Meetings
          </NavLink>
        </li>
      </ul>

      <div className="nav-section">
        <div className="nav-section-head">
          <span>Spaces</span>
          <button
            type="button"
            className="icon-btn"
            aria-label="New space"
            title="New space"
            onClick={() => setAddingSpace(true)}
          >
            +
          </button>
        </div>
        {addingSpace && (
          <InlineInput
            label="Space name"
            placeholder="Space name"
            onCancel={() => setAddingSpace(false)}
            onSubmit={async (name) => {
              try {
                const s = await createSpace(name);
                setCurrentSpace(s.id);
                setAddingSpace(false);
              } catch (e) {
                toastError(e, "Could not create space");
              }
            }}
          />
        )}
        {spaces === null && <p className="muted small pad">Loading…</p>}
        {spaces?.map((space) => {
          const cols = collections[space.id] ?? [];
          const isCollapsed = collapsed[space.id];
          return (
            <div key={space.id} className="space">
              <SpaceHead
                space={space}
                cols={cols}
                collapsed={!!isCollapsed}
                onToggle={() => {
                  setCollapsed((c) => ({ ...c, [space.id]: !c[space.id] }));
                  setCurrentSpace(space.id);
                }}
                onAddCollection={() => {
                  setAddingCollectionIn(space.id);
                  setCurrentSpace(space.id);
                  setCollapsed((c) => ({ ...c, [space.id]: false }));
                }}
                onDialog={setDialog}
                onNavigate={onNavigate}
              />
              {!isCollapsed && (
                <ul className="nav-list collections">
                  {cols.map((c) => (
                    <CollectionRow key={c.id} c={c} onNavigate={onNavigate} onDialog={setDialog} />
                  ))}
                  {cols.length === 0 && <li className="muted small pad">No collections</li>}
                </ul>
              )}
              {addingCollectionIn === space.id && (
                <InlineInput
                  label="Collection name"
                  placeholder="Collection name"
                  onCancel={() => setAddingCollectionIn(null)}
                  extra={
                    <select
                      aria-label="Collection kind"
                      value={newKind}
                      onChange={(e) => setNewKind(e.target.value as CollectionKind)}
                    >
                      <option value="list">List</option>
                      <option value="board">Board</option>
                      <option value="project">Project</option>
                      <option value="notebook">Notebook</option>
                      <option value="queue">Queue</option>
                    </select>
                  }
                  onSubmit={async (name) => {
                    try {
                      const d = await createCollection(space.id, name, newKind);
                      setAddingCollectionIn(null);
                      navigate(`/c/${d.collection.id}`);
                      onNavigate?.();
                    } catch (e) {
                      toastError(e, "Could not create collection");
                    }
                  }}
                />
              )}
            </div>
          );
        })}
        {spaces !== null && <ArchivedSection onNavigate={onNavigate} onDialog={setDialog} />}
      </div>
      {dialog && (
        <LifecycleDialog
          target={dialog}
          onChange={setDialog}
          onDeleted={(t) => {
            const ids =
              t.kind === "delete-space" ? t.collections.map((c) => c.id) : t.kind === "delete-collection" ? [t.collection.id] : [];
            const m = location.pathname.match(/^\/c\/([^/]+)/);
            if (m && ids.includes(m[1])) navigate("/");
          }}
        />
      )}

      <div className="sidebar-foot">
        <NavLink to="/settings" onClick={onNavigate}>
          <Icon name="settings" />
          Settings
        </NavLink>
        {admin && (
          <NavLink to="/admin" onClick={onNavigate} className="admin-link">
            <Icon name="shield" />
            Admin
          </NavLink>
        )}
        <UserMenu
          name={membership?.actor.display_name ?? me?.account.email ?? ""}
          email={me?.account.email ?? ""}
          admin={admin}
          onNavigate={onNavigate}
        />
      </div>
    </nav>
  );
}
